// src/components/TrainerDetail.tsx
import { useState, useEffect } from 'react';
import { bookingsAPI, trainersAPI, reviewsAPI } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { StripePaymentForm } from './StripePaymentForm.tsx';

interface TrainerDetailProps {
  trainerId: string;
  onClose: () => void;
  onNavigateToProfile?: (userId: string) => void;
}

interface TimeSlot {
  time: string;
  available: boolean;
}

interface AvailableSlots {
  date: string;
  slots: TimeSlot[];
}

interface Review {
  id: number;
  rating: number;
  comment: string;
  created_at: string;
  customer: {
    id: string;
    username: string;
    first_name: string;
    last_name?: string;
    profile_picture?: string;
  };
}

interface ReviewableBooking {
  booking_id: string;
  trainer: {
    id: string;
    name: string;
  };
  session_date: string;
  session_time: string;
  duration: number;
}

export function TrainerDetail({ trainerId, onClose, onNavigateToProfile }: TrainerDetailProps) {
  const { user } = useAuth();
  const [trainer, setTrainer] = useState<any>(null);
  const [availableSlots, setAvailableSlots] = useState<AvailableSlots[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [sessionDuration, setSessionDuration] = useState<number>(60);
  const [notes, setNotes] = useState<string>('');
  const [addressLine1, setAddressLine1] = useState<string>('');
  const [addressLine2, setAddressLine2] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [state, setState] = useState<string>('');
  const [zipCode, setZipCode] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [step, setStep] = useState<'details' | 'booking' | 'payment' | 'confirmation' | 'reviews' | 'leave-review'>('details');

  // Review-related state
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewableBookings, setReviewableBookings] = useState<ReviewableBooking[]>([]);
  const [selectedBookingForReview, setSelectedBookingForReview] = useState<string>('');
  const [reviewRating, setReviewRating] = useState<number>(5);
  const [reviewComment, setReviewComment] = useState<string>('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [canLeaveReview, setCanLeaveReview] = useState(false);
  const [reviewCount, setReviewCount] = useState<number>(0);

  useEffect(() => {
    fetchTrainerDetails();
    if (user && user.role === 'customer') {
      checkIfUserCanReview();
    }
    fetchTrainerReviews();
  }, [trainerId, user]);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate, sessionDuration]);


  const fetchTrainerDetails = async () => {
    try {
      const response = await trainersAPI.getById(trainerId);
      setTrainer(response.data);

      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const year = tomorrow.getFullYear();
      const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
      const day = String(tomorrow.getDate()).padStart(2, '0');
      setSelectedDate(`${year}-${month}-${day}`);
    } catch (error) {
      console.error('Failed to fetch trainer details:', error);
    } finally {
      setLoading(false);
    }
  };





  const checkIfUserCanReview = async () => {
    try {
      const response = await reviewsAPI.getReviewableBookings();
      const data = response.data;

      const trainerBookings = data.reviewable_bookings?.filter(
        (booking: ReviewableBooking) => booking.trainer.id === trainerId
      ) || [];

      setReviewableBookings(trainerBookings);
      setCanLeaveReview(trainerBookings.length > 0);

      if (trainerBookings.length > 0) {
        setSelectedBookingForReview(trainerBookings[0].booking_id);
      }
    } catch (error) {
      console.error('Failed to check review eligibility:', error);
      setCanLeaveReview(false);
    }
  };


  const formatCompleteAddress = (): string => {
    const parts = [
      addressLine1.trim(),
      addressLine2.trim(),
      city.trim(),
      state.trim(),
      zipCode.trim()
    ].filter(part => part.length > 0);

    // Format as: "123 Main St, Apt 2B, New York, NY 10001"
    return parts.join(', ');
  };

  const handleReviewerProfileClick = (userId: string) => {
    if (onNavigateToProfile) {
      onNavigateToProfile(userId);
    }
  };

  // Update fetchTrainerReviews to set the count
  const fetchTrainerReviews = async () => {
    try {

      const response = await reviewsAPI.getTrainerReviews(trainerId);
      const reviewsData = response.data.reviews || [];

      console.log('Review data from API:', reviewsData);
      console.log('First review customer data:', reviewsData[0]?.customer);

      setReviews(reviewsData);
      setReviewCount(reviewsData.length); // Set the actual review count

    } catch (error) {
      console.error('Failed to fetch reviews:', error);
      setReviews([]);
      setReviewCount(0);
    }
  };

  // Also fetch review count when component loads
  useEffect(() => {
    fetchTrainerDetails();
    if (user && user.role === 'customer') {
      checkIfUserCanReview();
    }
    // Fetch reviews to get the count
    fetchTrainerReviews();
  }, [trainerId, user]);

  // Update the button to show actual review count:
  <button
    onClick={() => {
      fetchTrainerReviews();
      setStep('reviews');
    }}
    className="flex-1 bg-blue-100 text-blue-700 py-2 rounded-lg hover:bg-blue-200 transition-colors font-medium"
  >
    View Reviews ({reviewCount})
  </button>

  // Replace your handleSubmitReview function:
  const handleSubmitReview = async () => {
    if (!selectedBookingForReview || reviewRating < 1 || reviewRating > 5) {
      alert('Please select a valid booking and rating');
      return;
    }

    setSubmittingReview(true);

    try {
      console.log('Submitting review using API service');
      const reviewData = {
        rating: reviewRating,
        comment: reviewComment.trim()
      };

      const response = await reviewsAPI.submitReview(selectedBookingForReview, reviewData);

      console.log('Review submitted successfully:', response.data);
      alert('Review submitted successfully!');

      setStep('details');
      setReviewComment('');
      setReviewRating(5);

      // Refresh the reviewable bookings and trainer details
      await checkIfUserCanReview();
      await fetchTrainerDetails();

    } catch (error) {
      console.error('Failed to submit review:', error);
      alert('Failed to submit review. Please try again.');
    } finally {
      setSubmittingReview(false);
    }
  };

  // Replace your checkIfUserCanReview function with this version that uses your API service:



  // Also update your handleSubmitReview function:






  const fetchAvailableSlots = async (date: string) => {
    try {
      const response = await trainersAPI.getAvailableSlots(trainerId, date, sessionDuration);
      const slots: TimeSlot[] = response.data.available_slots.map((time: string) => ({
        time,
        available: true
      }));
      setAvailableSlots([{ date, slots }]);
    } catch (error) {
      console.error('Failed to fetch available slots:', error);
      setAvailableSlots([{ date, slots: [] }]);
    }
  };

  const calculateCost = () => {
    if (!trainer) return { total: 0, platformFee: 5, trainerAmount: 0 };

    let total = 0;

    switch (sessionDuration) {
      case 30:
        total = trainer.rate_30_min || 0;
        break;
      case 60:
        total = trainer.hourly_rate || 0;
        break;
      case 90:
        total = trainer.rate_90_min || 0;
        break;
      case 120:
        total = trainer.rate_120_min || 0;
        break;
      default:
        total = (trainer.hourly_rate * sessionDuration) / 60;
    }

    const platformFee = 5;
    const trainerAmount = total - platformFee;

    return { total, platformFee, trainerAmount };
  };

  const checkForOverlappingBookings = async (selectedDate: string, selectedTime: string, duration: number): Promise<boolean> => {
    try {
      if (!user) return false;

      const response = await bookingsAPI.getMyBookings();
      const customerBookings = response.data.bookings.filter((booking: any) =>
        booking.customer.id === user.id &&
        ['pending', 'confirmed'].includes(booking.status)
      );

      const newBookingStart = new Date(`${selectedDate}T${selectedTime}`);
      const newBookingEnd = new Date(newBookingStart.getTime() + (duration * 60 * 1000));

      for (const booking of customerBookings) {
        const existingStart = new Date(`${booking.scheduled_date}T${booking.scheduled_time}`);
        const existingEnd = new Date(existingStart.getTime() + (booking.duration_minutes * 60 * 1000));

        const hasOverlap = newBookingStart < existingEnd && newBookingEnd > existingStart;

        if (hasOverlap) {
          return true;
        }
      }

      return false;

    } catch (error) {
      console.error('Error checking for overlapping bookings:', error);
      return false;
    }
  };

  const handleBooking = async () => {
    // Update validation to check required address fields
    if (!selectedDate || !selectedTime || !addressLine1.trim() || !city.trim() || !state || !zipCode.trim()) {
      alert('Please fill in all required fields including complete address');
      return;
    }

    if (!user) {
      alert('Please log in to book a session');
      return;
    }

    setBooking(true);

    try {
      const hasOverlap = await checkForOverlappingBookings(selectedDate, selectedTime, sessionDuration);

      if (hasOverlap) {
        alert('You already have a booking that conflicts with this time slot. Please choose a different time or wait until your current session ends.');
        setBooking(false);
        return;
      }

      setStep('payment');
    } catch (error) {
      console.error('Booking validation failed:', error);
      alert('Unable to validate booking. Please try again.');
    } finally {
      setBooking(false);
    }
  };

  const getNextWeekDates = (): Array<{ value: string; label: string }> => {
    const dates: Array<{ value: string; label: string }> = [];
    const today = new Date();

    for (let i = 1; i <= 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const localDateString = `${year}-${month}-${day}`;

      dates.push({
        value: localDateString,
        label: date.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric'
        })
      });
    }
    return dates;
  };

  const renderStars = (rating: number, interactive: boolean = false, onRatingChange?: (rating: number) => void) => {
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => interactive && onRatingChange && onRatingChange(star)}
            className={`text-xl ${interactive ? 'cursor-pointer hover:scale-110' : ''} ${star <= rating ? 'text-yellow-400' : 'text-gray-300'
              }`}
            disabled={!interactive}
          >
            ★
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-300 rounded w-3/4"></div>
            <div className="h-4 bg-gray-300 rounded w-1/2"></div>
            <div className="h-20 bg-gray-300 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!trainer) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 text-center">
          <p className="text-gray-600">Trainer not found</p>
          <button
            onClick={onClose}
            className="mt-4 bg-gray-500 text-white px-4 py-2 rounded-lg"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const cost = calculateCost();


  const convertTo12HourFormat = (time24: string): string => {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const minute = parseInt(minutes, 10);

    if (hour === 0) {
      return `12:${minutes} AM`;
    } else if (hour < 12) {
      return `${hour}:${minutes} AM`;
    } else if (hour === 12) {
      return `12:${minutes} PM`;
    } else {
      return `${hour - 12}:${minutes} PM`;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">
            {step === 'details' && 'Trainer Details'}
            {step === 'booking' && 'Book Session'}
            {step === 'payment' && 'Complete Payment'}
            {step === 'confirmation' && 'Booking Confirmed'}
            {step === 'reviews' && 'Reviews'}
            {step === 'leave-review' && 'Leave Review'}
          </h2>


          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {step === 'details' && (
          <div className="p-6">
            {/* Trainer Info */}
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-20 h-20 bg-gray-300 rounded-full overflow-hidden">
                {trainer.profile_picture ? (
                  <img src={trainer.profile_picture} alt={trainer.first_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#E6A756] to-[#D2824C] flex items-center justify-center text-white font-bold text-xl">
                    {trainer.first_name[0]}{trainer.last_name[0]}
                  </div>
                )}
              </div>
              <div>
                <h3 className="text-xl font-semibold">{trainer.first_name} {trainer.last_name}</h3>
                <p className="text-gray-600">${trainer.hourly_rate}/hour</p>
                <div className="flex items-center space-x-2 mt-1">
                  <span className="text-yellow-500">⭐</span>
                  <span>{trainer.rating} ({trainer.total_sessions} sessions)</span>
                </div>
              </div>
            </div>

            {/* Bio */}
            {trainer.bio && (
              <div className="mb-6">
                <h4 className="font-semibold mb-2">About</h4>
                <p className="text-gray-700">{trainer.bio}</p>
              </div>
            )}

            {/* Specializations */}
            <div className="mb-6">
              <h4 className="font-semibold mb-2">Specializations</h4>
              <div className="flex flex-wrap gap-2">
                {trainer.specializations.split(',').map((spec: string, index: number) => (
                  <span key={index} className="bg-[#B8C5A6] text-[#5D4037] px-3 py-1 rounded-full text-sm font-medium">
                    {spec.trim()}
                  </span>
                ))}
              </div>
            </div>

            {/* Certifications */}
            <div className="mb-6">
              <h4 className="font-semibold mb-2">Certifications</h4>
              <div className="flex flex-wrap gap-2">
                {trainer.certifications.split(',').map((cert: string, index: number) => (
                  <span key={index} className="bg-[#F8F4EE] text-[#5D4037] px-3 py-1 rounded-full text-sm font-medium border border-[#D4C4B0]">
                    {cert.trim()}
                  </span>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {user && user.role === 'customer' && (
                <button
                  onClick={() => setStep('booking')}
                  className="w-full bg-[#E6A756] text-[#5D4037] py-3 rounded-lg hover:bg-[#D2824C] transition-colors font-medium"
                >
                  Book a Session
                </button>
              )}

              {/* Reviews and Leave Review buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    fetchTrainerReviews();
                    setStep('reviews');
                  }}
                  className="flex-1 bg-[#F8F4EE] text-[#5D4037] py-2 rounded-lg hover:bg-[#F5EFE7] transition-colors font-medium border border-[#D4C4B0]"
                >
                  View Reviews ({reviewCount})
                </button>

                {user && user.role === 'customer' && canLeaveReview && (
                  <button
                    onClick={() => setStep('leave-review')}
                    className="flex-1 bg-[#9CAF88] text-[#FEFCF8] py-2 rounded-lg hover:bg-[#7A8471] transition-colors font-medium"
                  >
                    Leave Review
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {step === 'reviews' && (
          <div className="p-6">
            <button
              onClick={() => setStep('details')}
              className="mb-4 text-[#8B5A3C] hover:text-[#5D4037] flex items-center font-medium"
            >
              ← Back to Profile
            </button>

            {reviews.length > 0 ? (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <div key={review.id} className="border-b pb-4 last:border-b-0">
                    <div className="flex items-start space-x-3 mb-2">
                      {/* Clickable Profile Picture */}
                      <div
                        className="w-10 h-10 bg-gray-300 rounded-full overflow-hidden flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all"
                        onClick={() => handleReviewerProfileClick(review.customer.id)}
                      >
                        {review.customer.profile_picture ? (
                          <img
                            src={review.customer.profile_picture}
                            alt={review.customer.first_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-[#9CAF88] to-[#7A8471] flex items-center justify-center text-white font-bold text-sm">
                            {review.customer.first_name[0]}
                            {review.customer.last_name?.[0] || ''}
                          </div>
                        )}
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <div>
                            {/* Clickable Name */}
                            <div
                              className="flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-[#E6A756] transition-all"
                              onClick={() => handleReviewerProfileClick(review.customer.id)}
                            >
                              {review.customer.first_name} {review.customer.last_name || ''}
                            </div>
                            <div className="text-sm text-gray-500">
                              {new Date(review.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          {renderStars(review.rating)}
                        </div>
                        {review.comment && (
                          <p className="text-gray-700 mt-2">{review.comment}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No reviews yet</p>
                <p className="text-sm text-gray-400 mt-1">
                  Be the first to leave a review after booking a session!
                </p>
              </div>
            )}
          </div>
        )}

        {step === 'leave-review' && (
          <div className="p-6">
            <button
              onClick={() => setStep('details')}
              className="mb-4 text-blue-600 hover:text-blue-800 flex items-center"
            >
              ← Back to Profile
            </button>

            <div className="space-y-6">
              {/* Select which session to review */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Session to Review
                </label>
                <select
                  value={selectedBookingForReview}
                  onChange={(e) => setSelectedBookingForReview(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E6A756] focus:border-transparent"
                >
                  {reviewableBookings.map((booking) => (
                    <option key={booking.booking_id} value={booking.booking_id}>
                      {new Date(booking.session_date).toLocaleDateString()} at {convertTo12HourFormat(booking.session_time)}
                      ({booking.duration} min)
                    </option>
                  ))}
                </select>
              </div>

              {/* Rating */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rating
                </label>
                {renderStars(reviewRating, true, setReviewRating)}
              </div>

              {/* Comment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comment (Optional)
                </label>
                <textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  placeholder="Share your experience with this trainer..."
                  rows={4}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E6A756] focus:border-transparent"
                />
              </div>

              {/* Submit Button */}
              <button
                onClick={handleSubmitReview}
                disabled={submittingReview || !selectedBookingForReview}
                className="w-full bg-[#9CAF88] text-[#FEFCF8] py-3 rounded-lg hover:bg-[#7A8471] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {submittingReview ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </div>
        )}

        {/* Rest of your existing booking and confirmation steps remain the same */}
        {step === 'booking' && (
          // ... existing booking step code ...
          <div className="p-6 space-y-6">
            {/* All your existing booking form code stays the same */}
            {/* Session Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Session Duration</label>
              <select
                value={sessionDuration}
                onChange={(e) => setSessionDuration(Number(e.target.value))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E6A756] focus:border-transparent"
              >
                <option value={30}>30 minutes</option>
                <option value={60}>1 hour</option>
                <option value={90}>1.5 hours</option>
                <option value={120}>2 hours</option>
              </select>
            </div>

            {/* Date Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Date</label>
              <div className="grid grid-cols-2 gap-2">
                {getNextWeekDates().map((date) => (
                  <button
                    key={date.value}
                    onClick={() => setSelectedDate(date.value)}
                    className={`p-3 text-sm rounded-lg border transition-colors ${selectedDate === date.value
                      ? 'bg-[#E6A756] text-[#5D4037] border-[#E6A756]'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-[#D4C4B0]'
                      }`}
                  >
                    {date.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Time Selection */}
            {selectedDate && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Available Times</label>
                {availableSlots[0]?.slots.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2">
                    {availableSlots[0].slots.map((slot) => (
                      <button
                        key={slot.time}
                        onClick={() => setSelectedTime(slot.time)}
                        disabled={!slot.available}
                        className={`p-2 text-sm rounded-lg border transition-colors ${selectedTime === slot.time
                          ? 'bg-[#E6A756] text-[#5D4037] border-[#E6A756]'
                          : slot.available
                            ? 'bg-white text-gray-700 border-gray-300 hover:border-[#D4C4B0]'
                            : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                          }`}
                      >
                        {convertTo12HourFormat(slot.time)}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No available times for this date</p>
                )}
              </div>
            )}

            {/* Customer Address - Structured Inputs */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Training Location *</label>

              {/* Address Line 1 */}
              <div className="space-y-3">
                <input
                  type="text"
                  value={addressLine1}
                  onChange={(e) => setAddressLine1(e.target.value)}
                  placeholder="Street address (e.g., 123 Main Street)"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E6A756] focus:border-transparent"
                  required
                />

                {/* Address Line 2 */}
                <input
                  type="text"
                  value={addressLine2}
                  onChange={(e) => setAddressLine2(e.target.value)}
                  placeholder="Apt, suite, unit, building, floor, etc. (optional)"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E6A756] focus:border-transparent"
                />

                {/* City, State, ZIP Row */}
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="City"
                    className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E6A756] focus:border-transparent"
                    required
                  />

                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E6A756] focus:border-transparent"
                      required
                    >
                      <option value="">State</option>
                      <option value="AL">AL</option>
                      <option value="AK">AK</option>
                      <option value="AZ">AZ</option>
                      <option value="AR">AR</option>
                      <option value="CA">CA</option>
                      <option value="CO">CO</option>
                      <option value="CT">CT</option>
                      <option value="DE">DE</option>
                      <option value="FL">FL</option>
                      <option value="GA">GA</option>
                      <option value="HI">HI</option>
                      <option value="ID">ID</option>
                      <option value="IL">IL</option>
                      <option value="IN">IN</option>
                      <option value="IA">IA</option>
                      <option value="KS">KS</option>
                      <option value="KY">KY</option>
                      <option value="LA">LA</option>
                      <option value="ME">ME</option>
                      <option value="MD">MD</option>
                      <option value="MA">MA</option>
                      <option value="MI">MI</option>
                      <option value="MN">MN</option>
                      <option value="MS">MS</option>
                      <option value="MO">MO</option>
                      <option value="MT">MT</option>
                      <option value="NE">NE</option>
                      <option value="NV">NV</option>
                      <option value="NH">NH</option>
                      <option value="NJ">NJ</option>
                      <option value="NM">NM</option>
                      <option value="NY">NY</option>
                      <option value="NC">NC</option>
                      <option value="ND">ND</option>
                      <option value="OH">OH</option>
                      <option value="OK">OK</option>
                      <option value="OR">OR</option>
                      <option value="PA">PA</option>
                      <option value="RI">RI</option>
                      <option value="SC">SC</option>
                      <option value="SD">SD</option>
                      <option value="TN">TN</option>
                      <option value="TX">TX</option>
                      <option value="UT">UT</option>
                      <option value="VT">VT</option>
                      <option value="VA">VA</option>
                      <option value="WA">WA</option>
                      <option value="WV">WV</option>
                      <option value="WI">WI</option>
                      <option value="WY">WY</option>
                    </select>

                    <input
                      type="text"
                      value={zipCode}
                      onChange={(e) => setZipCode(e.target.value)}
                      placeholder="ZIP"
                      maxLength={10}
                      className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E6A756] focus:border-transparent"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Address Preview */}
              {(addressLine1 || city || state || zipCode) && (
                <div className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-600">
                  <strong>Address Preview:</strong> {formatCompleteAddress()}
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes (Optional)</label>

              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any specific goals or requirements for this session..."
                rows={3}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E6A756] focus:border-transparent"
              />
            </div>

            {/* Cost Breakdown */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold mb-3">Cost Breakdown</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Session ({sessionDuration} min)</span>
                  <span>${cost.total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Platform fee</span>
                  <span>${cost.platformFee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Trainer receives</span>
                  <span>${cost.trainerAmount.toFixed(2)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-semibold">
                  <span>Total</span>
                  <span>${cost.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={() => setStep('details')}
                className="flex-1 bg-[#F8F4EE] text-[#5D4037] py-3 rounded-lg hover:bg-[#F5EFE7] transition-colors font-medium border border-[#D4C4B0]"
              >
                Back
              </button>
              <button
                onClick={handleBooking}
                disabled={booking || !selectedDate || !selectedTime || !addressLine1.trim() || !city.trim() || !state || !zipCode.trim()}
                className="flex-1 bg-[#E6A756] text-[#5D4037] py-3 rounded-lg hover:bg-[#D2824C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {booking ? 'Validating...' : 'Continue to Payment'}
              </button>
            </div>
          </div>
        )}

        {step === 'payment' && (
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">Complete Payment</h3>

            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h4 className="font-semibold mb-2">Booking Summary</h4>
              <div className="space-y-1 text-sm">
                <p><strong>Trainer:</strong> {trainer.first_name} {trainer.last_name}</p>
                <p><strong>Date:</strong> {new Date(selectedDate).toLocaleDateString()}</p>
                <p><strong>Time:</strong> {convertTo12HourFormat(selectedTime)}</p>
                <p><strong>Duration:</strong> {sessionDuration} minutes</p>
                <p><strong>Location:</strong> {formatCompleteAddress()}</p>
                <div className="border-t pt-2 mt-2">
                  <p className="font-semibold">Total: ${cost.total.toFixed(2)}</p>
                </div>
              </div>
            </div>

            <StripePaymentForm
              bookingData={{
                trainer_id: trainerId,
                scheduled_date: selectedDate,
                scheduled_time: selectedTime,
                duration_minutes: sessionDuration,
                customer_address: formatCompleteAddress(),
                notes: notes
              }}
              amount={cost.total}
              onSuccess={() => setStep('confirmation')}
              onCancel={() => setStep('booking')}
            />
          </div>
        )}

        {step === 'confirmation' && (
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-[#B8C5A6] rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-[#5D4037]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2">Payment Successful!</h3>
            <p className="text-gray-600 mb-4">
              Your booking request has been submitted for {selectedDate} at {convertTo12HourFormat(selectedTime)}.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              The trainer will review your request and confirm shortly. You'll receive an email notification.
            </p>
            <button
              onClick={onClose}
              className="bg-[#E6A756] text-[#5D4037] px-6 py-3 rounded-lg hover:bg-[#D2824C] transition-colors font-medium"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}